import type { TransitionLogSignature } from '../../types/tuition-assistance';
import type { TasAssistance } from './tas-assistance.type.ts';
export interface TasAssistanceExtension extends TasAssistance {}
export class TasAssistanceExtension {
  // ── Computed Properties ──
  get isSettled() {
    return this.state === 'TAS.AssistanceState.SETTLED';
  }

  get isCaptured() {
    return this.state === 'TAS.AssistanceState.CAPTURED';
  }

  get isAuthorized() {
    return this.state === 'TAS.AssistanceState.AUTHORIZED';
  }

  get isCourseCash() {
    return this.manner === 'TAS.AssistanceManner.COURSE_CASH';
  }

  get isExpired() {
    return this.state === 'TAS.AssistanceState.EXPIRED';
  }

  // True once this reserve has been through the one-time "final chance" reactivation
  // (its transition log carries a reactivate event). Matches the backend
  // CourseCash::ExpiryCalculator::REACTIVATE_EVENTS set.
  get previouslyReactivated() {
    return (this.transitionLog ?? []).some(
      (log) => log.event === 'reactivate' || log.event === 'reactivate!'
    );
  }

  get hasCardUsageProof(): boolean {
    // Check displayClassification which returns classification || assetClassification
    // The asset-classification comes from the TasAsset, not the Attachment join record
    return (
      this.attachments?.some(
        (attachment) => attachment.displayClassification === 'CARD_USAGE_PROOF'
      ) ?? false
    );
  }

  get needsReceipt() {
    return this.isCourseCash && this.isCaptured && !this.hasCardUsageProof;
  }

  get hasReceiptUploaded() {
    return this.hasCardUsageProof;
  }

  get capturedDate(): Date | null {
    const capturedTransition = this.transitionLog?.find(
      (log: TransitionLogSignature) => log.to === 'TAS.AssistanceState.CAPTURED'
    );
    return capturedTransition ? new Date(capturedTransition.timestamp) : null;
  }

  get authorizedDate(): Date | null {
    const authorizedTransition = this.transitionLog?.find(
      (log: TransitionLogSignature) => log.to === 'TAS.AssistanceState.AUTHORIZED'
    );
    return authorizedTransition ? new Date(authorizedTransition.timestamp) : null;
  }

  get receiptDueDate(): Date | null {
    const captured = this.capturedDate;
    if (!captured) return null;

    const dueDate = new Date(captured);
    dueDate.setDate(dueDate.getDate() + 21); // 21 days after capture
    return dueDate;
  }

  get daysUntilReceiptDue(): number | null {
    const due = this.receiptDueDate;
    if (!due) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDay = new Date(due);
    dueDay.setHours(0, 0, 0, 0);

    const diffTime = dueDay.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  get settledDate(): string | null {
    const settledTransition = this.transitionLog?.find(
      (log: TransitionLogSignature) => log.to === 'TAS.AssistanceState.SETTLED'
    );

    return settledTransition
      ? ((settledTransition.reason?.payment?.date || settledTransition.timestamp) as string)
      : null;
  }

  get courseCashApprovables() {
    return (this.approvables ?? []).filter((a) => a.isCourseCashUsageReview);
  }
}

const Registration = {
  kind: 'object',
  name: 'tas-assistance',
  features: TasAssistanceExtension,
};
export default Registration;
